import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import useAccountWasteReceipts from "../../data/useAccountWasteReceipts";
import { IAccount } from "../../models/account/IAccount";
import formatDateTime from "../../utils/formatDateTime";
import openInNewTab from "../../utils/openInNewTab";
import ClickToCopy from "../shared/ClickToCopy";
import LoadingWheel from "../shared/LoadingWheel";
import WasteReceiptStatusPill from "../wasteTracking/WasteReceiptStatusPill";

// Waste Movements tab on a haulier account: every DEFRA receipt that account delivered.
export default function AccountWasteReceiptList({ account }: { account: IAccount }) {
  const { t } = useTranslation("accounts");
  const { accountWasteReceipts } = useAccountWasteReceipts(account.id, "AccountWasteReceiptList");

  if (accountWasteReceipts.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <LoadingWheel />
      </div>
    );
  }

  if (accountWasteReceipts.isError || !accountWasteReceipts.data?.data) {
    return (
      <div className="flex items-center justify-center py-10">
        <p className="text-xl font-bold text-red-600">{t("Error loading waste movements")}</p>
      </div>
    );
  }

  const rows = accountWasteReceipts.data.data;

  if (rows.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-gray-500">
        {t("No waste movements have been recorded against this haulier yet. Pick them as the haulier on a Waste Tracking row and it will appear here.")}
      </p>
    );
  }

  return (
    <div className="overflow-auto">
      <p className="p-2 text-center text-xs text-gray-500 sm:hidden">{t("Tap a row to open the lead")}</p>
      <table className="min-w-full divide-y divide-gray-300">
        <thead>
          <tr className="bg-primary-50 text-gray-800">
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">{t("Vehicle")}</th>
            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold sm:table-cell">{t("Collected", { context: "date" })}</th>
            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold sm:table-cell">{t("Received")}</th>
            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold sm:table-cell">{t("Weight")}</th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">{t("Status")}</th>
            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">{t("Waste tracking ID")}</th>
            <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold sm:table-cell">{t("Submitted")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row) => (
            <tr
              key={row.vehicleId}
              className={row.leadId ? "cursor-pointer hover:bg-gray-50" : ""}
              onClick={() => row.leadId && openInNewTab(`leads/${row.leadId}`)}
            >
              <td className="px-3 py-3 text-sm">
                <p className="font-medium text-gray-900">{row.vrm ?? t("Unknown VRM")}</p>
                <p className="text-xs text-gray-500">{[row.make, row.model].filter(Boolean).join(" ")}</p>
                {row.stockNumber && (
                  <p className="text-xs text-gray-500">{t("Stock {{stockNumber}}", { stockNumber: row.stockNumber })}</p>
                )}
              </td>
              <td className="hidden px-3 py-3 text-sm text-gray-700 sm:table-cell">
                {formatDateTime(row.collectedOn, DateTime.DATETIME_SHORT, "-")}
              </td>
              <td className="hidden px-3 py-3 text-sm text-gray-700 sm:table-cell">
                {formatDateTime(row.dateTimeReceived, DateTime.DATETIME_SHORT, "-")}
              </td>
              <td className="hidden px-3 py-3 text-sm text-gray-700 sm:table-cell">
                {row.weightKg === null
                  ? "-"
                  : row.weightIsEstimate
                    ? t("{{weight}} kg (est.)", { weight: row.weightKg })
                    : t("{{weight}} kg", { weight: row.weightKg })}
              </td>
              <td className="px-3 py-3 text-sm">
                <WasteReceiptStatusPill row={row} />
              </td>
              <td className="px-3 py-3 text-sm">
                {row.wasteTrackingId ? <ClickToCopy text={row.wasteTrackingId} /> : <span className="text-gray-400">-</span>}
              </td>
              <td className="hidden px-3 py-3 text-sm text-gray-700 sm:table-cell">
                {formatDateTime(row.submittedOn, DateTime.DATETIME_SHORT, "-")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
